import { type ReactNode, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { createAlert, listAlerts } from '../lib/rates'

/** Pro-tier lane watch. Signed-out → nudge to sign in; free cap → nudge to upgrade. */
export function WatchLaneButton({
  pol,
  pod,
  polName,
  podName,
}: { pol: string; pod: string; polName?: string | null; podName?: string | null }) {
  const { session } = useAuth()
  const [state, setState] = useState<'idle' | 'saving' | 'watching'>('idle')
  const [msg, setMsg] = useState<ReactNode>(null)

  useEffect(() => {
    if (!session) return
    listAlerts()
      .then((as) => {
        if (as.some((a) => a.pol_code === pol && a.pod_code === pod)) setState('watching')
      })
      .catch(() => {})
  }, [session, pol, pod])

  async function watch() {
    if (!session) return
    setState('saving')
    setMsg(null)
    const { error } = await createAlert({
      user_id: session.user.id,
      pol_code: pol,
      pod_code: pod,
      pol_name: polName,
      pod_name: podName,
    })
    if (!error) {
      setState('watching')
      setMsg("We'll email you when this lane moves.")
    } else if (error === 'exists') {
      setState('watching')
    } else if (error === 'limit') {
      setState('idle')
      setMsg(
        <>
          Free plan includes 1 alert.{' '}
          <Link to="/pricing" className="font-semibold text-link hover:underline">
            Upgrade to Pro
          </Link>{' '}
          for unlimited.
        </>
      )
    } else {
      setState('idle')
      setMsg('Could not save. Please try again.')
    }
  }

  if (!session) {
    return (
      <div className="rounded-xl border border-border bg-surface-0 p-4">
        <div className="text-sm font-semibold text-ink">Watch this lane</div>
        <p className="mt-1 text-xs text-muted">
          Get an email the moment this rate moves.{' '}
          <Link to="/account" className="text-link hover:underline">
            Sign in
          </Link>
          , it's free.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border bg-surface-0 p-4">
      <button
        type="button"
        onClick={watch}
        disabled={state === 'saving' || state === 'watching'}
        className={`w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
          state === 'watching'
            ? 'border border-signal/40 bg-signal-soft text-signal'
            : 'border border-border-strong text-ink hover:border-signal'
        }`}
      >
        {state === 'watching' ? '✓ Watching this lane' : state === 'saving' ? 'Saving…' : '🔔 Watch this lane'}
      </button>
      {msg && <p className="mt-2 text-xs text-muted">{msg}</p>}
    </div>
  )
}
